import {FC, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {AnimatePresence} from 'framer-motion';

// Pages
import AboutPage from '@/src/pages/AboutPage';
import MachPage from '@/src/pages/MachPage';
import NexusPage from '@/src/pages/NexusPage';
import BlogPage from '@/src/pages/BlogPage';
import BlogPostPage from '@/src/pages/BlogPostPage';

import ServicesPage from '@/src/pages/ServicesPage';

import WorkCaseStudyPage from '@/src/pages/WorkCaseStudyPage';

import OverlayCard from '@/components/Overlay/OverlayCard';
import {isOverlayRoute} from '@/lib/navigationRoutes';

type StackEntry = {
  key: string;
  pathname: string;
  location: any; // react-router Location (avoid typing import collisions)
};

const OverlayStack: FC = () => {
  const location = useLocation() as any;
  const pathname = (location as any).pathname as string;

  const [stack, setStack] = useState<StackEntry[]>([]);
  const [closingAll, setClosingAll] = useState(false);
  const prevPathRef = useRef<string | null>(null);

  // Push/pop logic based on route changes
  useLayoutEffect(() => {
    const isOverlay = isOverlayRoute(pathname);
    const prevPath = prevPathRef.current;
    prevPathRef.current = pathname;

    if (isOverlay) {
      setClosingAll(false);
      setStack((prev) => {
        const top = prev[prev.length - 1];
        if (top && top.pathname === pathname) {
          // Same route again: replace top location (query/hash could change)
          return prev.map((e, i) => (i === prev.length - 1 ? {...e, location} : e));
        }
        const entry: StackEntry = {
          key: `${pathname}-${Date.now()}`,
          pathname,
          location,
        };
        // keep at most 2 items behind to show subtle depth
        const keptBehind = prev.slice(-2);
        return [...keptBehind, entry];
      });
    } else {
      // Navigated to landing or another non-overlay route: animate dismissal by unmounting overlays
      setClosingAll(true);
      setStack((prev) => (prev.length ? [] : prev));
    }
  }, [pathname]);

  // When the top exits and we are closing all, clear stack
  const handleExitComplete = () => {
    if (closingAll) {
      setStack([]);
      setClosingAll(false);
    }
  };

  // Map a pathname to its component element
  const renderForPath = (pathname: string) => {
    if (pathname.startsWith('/blog/')) {
      const slug = pathname.replace('/blog/', '');
      return <BlogPostPage/>;
    }
    if (pathname.startsWith('/work/')) {
      return <WorkCaseStudyPage />;
    }
    switch (pathname) {
      case '/about':
        return <AboutPage/>;
      case '/services':
        return <ServicesPage/>;
      case '/services/mach':
        return <MachPage/>;
      case '/services/nexus':
        return <NexusPage/>;
      case '/blog':
        return <BlogPage/>;

      default:
        return null;
    }
  };

  // Render overlay cards for each stack entry. Only the last is "top".
  const items = stack;
  const lastIndex = items.length - 1;

  if (items.length === 0 && !closingAll) return null;

  return (
    <AnimatePresence mode="sync" onExitComplete={handleExitComplete}>
      {/* Placeholder behind card when first overlay opens, so initial nav shows depth */}
      {items.length === 1 && (
        <OverlayCard variant={'behind' as any} behindIndex={1} key={items[0].key + '-base-behind'}>
          <div className="h-full w-full"/>
        </OverlayCard>
      )}
      {items.map((entry, index) => {
        const isTop = index === lastIndex;
        const behindIndex = isTop ? 0 : lastIndex - index; // 1 for just-behind, 2 for second, etc.
        const variant = isTop ? 'top' : 'behind';

        const backgroundLocation = entry.location?.state?.backgroundLocation ?? null;

        const element = renderForPath(entry.pathname);
        if (!element) return null;
        return (
          <OverlayCard
            key={entry.key}
            variant={variant as any}
            behindIndex={behindIndex}
            backgroundLocation={isTop ? backgroundLocation : undefined}
          >
            {element}
          </OverlayCard>
        );
      })}
    </AnimatePresence>
  );
};

export default OverlayStack;
